import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { Observable } from "rxjs";

import { Option } from "@shared/Entities/General/option.entity";
import { Perfil } from "@shared/Entities/Perfil/perfil.entity";
import { Rol } from "@shared/Entities/Rol/rol.entity";
import { ProfileName } from "@shared/Entities/Usuario/profile-name.enum";
import { CompanyAlly } from "@shared/Interfaces/company-ally.interface";
import { PerfilRepository } from "@shared/Repositories/perfil.repository";
import { RolesRepository } from "@shared/Repositories/roles.repository";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { SharedOptionsService } from "@shared/Services/shared-options.service";

import { AccessField } from "../../Types/Enums/access-field.enum";
import { NewUserAccessData } from "../../Types/new-user";
import { UserCreationService } from "../../user-creation.service";

@Component({
  selector: "app-access-form",
  templateUrl: "./access-form.component.html",
})
export class AccessFormComponent implements OnInit {
  public form: UntypedFormGroup;
  public profiles$: Observable<Perfil[]>;
  public roles$: Observable<Rol[]>;
  public provinces$: Observable<Option[]>;
  public readonly ProfileName = ProfileName;
  public isUserCanales: boolean;
  public currentProfile: Perfil;
  public companyAllies$: Observable<CompanyAlly[]>;

  private previousProfileName: string | null = null;

  private _isEditing: boolean = false;

  @Output() public submitForm = new EventEmitter<NewUserAccessData>();

  @Input() public set isEditing(editing: boolean) {
    this._isEditing = editing;
    if (editing) {
      this.form?.get("userCode").disable();
    } else {
      this.form?.get("userCode").enable();
    }
  }
  public get isEditing(): boolean {
    return this._isEditing;
  }

  @Input() public set accessData(newAccessData: NewUserAccessData | null) {
    this.requiredFields = {};
    this.previousProfileName = null;

    if (!newAccessData) {
      this.form.reset();

      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control.clearValidators();
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });

      return;
    }

    this.form.patchValue(newAccessData);

    if (newAccessData?.roles) {
      const roles = newAccessData.roles.map((role) => {
        return {
          id: role.id,
          nombre: role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    }
  }

  public readonly AccessField = AccessField;
  public requiredFields: Partial<Record<AccessField, boolean>> = {};

  constructor(
    private formBuilder: UntypedFormBuilder,
    private profileService: PerfilRepository,
    private rolesService: RolesRepository,
    private userCreationService: UserCreationService,
    private sharedOptionsService: SharedOptionsService,
    private userRepository: UsuariosRepository
  ) {}

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      company: ["", this.isRequired(AccessField.Company)],
      empresaUnitId: [false],
      userCode: ["", this.isRequired(AccessField.UserCode)],
      clientCode: ["", this.isRequired(AccessField.ClientCode)],
      intermediaryCode: ["", this.isRequired(AccessField.IntermediaryCode)],
      clientCodeUnit: ["", this.isRequired(AccessField.ClientCodeUnit)],
      intermediaryCodeUnit: ["", this.isRequired(AccessField.IntermediaryCodeUnit)],
      identificationProfile: ["", this.isRequired(AccessField.ProfileIdentification)],
      officeCode: ["", this.isRequired(AccessField.OfficeCode)],
      profile: [null, Validators.required],
      roles: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      healthProviderCode: ["", this.isRequired(AccessField.HealthProviderCode)],
      isUserCanales: [false],
      isUserAdmin: [false],
      fiduciaryRnc: ["", this.isRequired(AccessField.FiduciaryRnc)],
    });

    if (this.isEditing) {
      this.form.get("userCode").disable();
    }

    this.roles$ = this.rolesService.getRoles();
    this.profiles$ = this.profileService.getPerfiles();
    this.requiredFields = this.userCreationService.getRequiredFields();
    this.provinces$ = this.sharedOptionsService.getProvinces();
    this.companyAllies$ = this.sharedOptionsService.getCompanyAlly();

    this.form.get("profile").valueChanges.subscribe((profile: Perfil) => {
      this.updateValidatorsForProfileChange(profile?.nombre);
      const roles = profile?.perfilRole?.map((role) => {
        return {
          id: role.roleId,
          nombre: role.role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    });

    this.mapFormData();
  }

  private updateValidatorsForProfileChange(newProfileName: string): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.clearValidators();
      this.form.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });

    if (this.previousProfileName) {
      const previousRequiredFields = this.userCreationService.getRequiredFields(this.previousProfileName);
      this.clearValidatorsForFields(previousRequiredFields);
    }

    this.requiredFields = this.userCreationService.getRequiredFields(newProfileName);
    this.applyValidatorsForFields(this.requiredFields);

    this.previousProfileName = newProfileName;
    this.refreshFormControls(this.form.controls);
  }

  private clearValidatorsForFields(fields: Partial<Record<AccessField, boolean>>): void {
    Object.keys(fields).forEach((key) => {
      if (fields[key]) {
        this.form.get(key)?.clearValidators();
        this.form.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    });
  }

  private applyValidatorsForFields(fields: Partial<Record<AccessField, boolean>>): void {
    Object.keys(fields).forEach((key) => {
      if (fields[key]) {
        this.form.get(key)?.setValidators(Validators.required);
        this.form.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    });
  }

  public mapRoles(): void {
    if (this.currentProfile && this.currentProfile?.id > 0) {
      const profileControl = this.form.get("profile");
      profileControl?.setValue(this.currentProfile);

      this.requiredFields = this.userCreationService.getRequiredFields(profileControl?.value?.nombre);
      const roles = profileControl?.value?.perfilRole?.map((role) => {
        return {
          id: role.roleId,
          nombre: role.role.nombre,
        };
      });
      this.form.get("roles").setValue(roles);
    }
  }

  public mapFormData(): void {
    this.userRepository.getInfoClienteActual().subscribe((usuario) => {
      this.isUserCanales = this.checkIfUserCanales(usuario);

      const userAdminPermission = this.getUserAdminPermission(usuario);
      if (userAdminPermission) {
        this.setCurrentProfile(usuario);
        this.mapRoles();
        this.mapMetadata(usuario.metadata);
      }
    });
  }

  private checkIfUserCanales(usuario: any): boolean {
    return usuario.usuarioRole.some((x) => x.role.nombre === "Canales");
  }

  private getUserAdminPermission(usuario: any): any {
    return usuario.usuarioRole.find((x) => x.role.nombre === "User Admin");
  }

  private setCurrentProfile(usuario: any): void {
    this.profiles$.subscribe((profiles) => {
      this.currentProfile = profiles.find((x) => x.nombre === usuario?.perfil.nombre);
    });
  }

  private mapMetadata(metadata: any): void {
    if (!metadata) return;

    this.setFormValue("company", metadata.empresa);

    const { codigoPrestador, codigoIntermediario, codigoIntermediarioUnit } = metadata;
    this.mapCodigoPrestador(codigoPrestador);
    this.mapCodigoIntermediario(codigoIntermediario);
    this.mapCodigoIntermediarioUnit(codigoIntermediarioUnit);
  }

  private setFormValue(controlName: string, value: any): void {
    if (value) {
      this.form.get(controlName)?.setValue(value);
    }
  }

  private mapCodigoPrestador(codigoPrestador: string): void {
    if (codigoPrestador) {
      const healthProviderCode = codigoPrestador.length === 5 ? codigoPrestador.slice(1) : codigoPrestador;
      this.setFormValue("healthProviderCode", healthProviderCode);
      this.setFormValue("identificationProfile", codigoPrestador);
    }
  }

  private mapCodigoIntermediario(codigoIntermediario: string): void {
    if (codigoIntermediario) {
      const intermediaryCode = codigoIntermediario.length === 5 ? codigoIntermediario.slice(1) : codigoIntermediario;
      this.setFormValue("intermediaryCode", intermediaryCode);
    }
  }

  private mapCodigoIntermediarioUnit(codigoIntermediarioUnit: string): void {
    if (codigoIntermediarioUnit) {
      const intermediaryCodeUnit =
        codigoIntermediarioUnit.length === 5 ? codigoIntermediarioUnit.slice(1) : codigoIntermediarioUnit;
      this.setFormValue("intermediaryCodeUnit", intermediaryCodeUnit);
    }
  }

  private refreshFormControls(controls: Record<string, AbstractControl>): void {
    Object.values(controls).forEach((control) => control.updateValueAndValidity({ onlySelf: true, emitEvent: false }));
  }

  private isRequired(field: AccessField[] | AccessField): ValidatorFn {
    return (control): ValidationErrors | null => {
      const fields = Array.isArray(field) ? field : [field];
      const isRequired = fields.some((f) => this.requiredFields[f]);
      return isRequired ? Validators.required(control) : null;
    };
  }

  public onSubmit(): void {
    const formValue = this.form.value;
    const value = Object.entries(formValue).reduce((acc, [key, currentValue]) => {
      if (this.requiredFields[key] !== null && this.requiredFields[key] !== undefined) {
        acc[key] = currentValue;
      }
      return acc;
    }, {});

    if (formValue.isUserCanales === null) formValue.isUserCanales = false;
    if (formValue.isUserAdmin === null) formValue.isUserAdmin = false;

    const submitValue = {
      ...value,
      empresaUnitId: formValue.empresaUnitId?.id,
      email: formValue.email,
      roles: formValue.roles,
      profile: formValue.profile,
      isUserCanales: formValue.isUserCanales,
      isUserAdmin: formValue.isUserAdmin,
    };

    this.submitForm.emit(submitValue as NewUserAccessData);
  }

  public showField(field: AccessField): boolean {
    return Object.keys(this.requiredFields).includes(String(field));
  }

  public get profileName(): AbstractControl {
    return this.form.get("profile").value;
  }

  public get isUserCanalesControl(): AbstractControl {
    return this.form.get("isUserCanales").value;
  }

  public get isUserAdminControl(): AbstractControl {
    return this.form.get("isUserAdmin").value;
  }
}
